import React, { useEffect, useState } from "react";
import { findLinkByAlias } from "../../../helpers/GiftCardHelper";
import { isNotNullOrEmpty } from "../../../helpers/Utils";
import { IActivateBasketFullModel } from "../../../models/ActivateGiftCard.model";
import { CheckoutBillingAddressData } from "../../../models/ElavonCheckoutModels";
import { CheckoutStatusEnum } from "../../../models/GiftCardCheckoutResult.model";
import { ICheckoutSessionData } from "../../../models/PaymentStep.model";
import { PaymentTypeEnum } from "../../../models/enums/PaymentTypeEnum";
import DataService from "../../../services/DataService.service";
import { AppSettingContext } from "../../Application";
import LoadingComponent from "../../shared/LoadingComponent";
import ElavonPayment, { HandleElavonCheckout, HandleThreeDSecureAuth } from "../shared/ElavonPayment";

interface IPaymentTypesStepProps {
    loadAmount: string;
    basketData: IActivateBasketFullModel;
    isSectionActive: boolean;
}

const LoadAndPayForm = (props: IPaymentTypesStepProps) => {
    const { labels, links, elavonSettings } = React.useContext(AppSettingContext);

    const termsPageUrl = findLinkByAlias("terms-page", links);
    const termsLink = labels.get("GiftCard.App.Steps.PreviewAndPay.TermsAndConditions").replace("{0}", termsPageUrl.url ?? "");

    //#region STATE PROPERTIES

    const [isLoading, setIsLoading] = useState(false);
    const [isCardPaymentLoading, setIsCardPaymentLoading] = useState(false);
    const [paymentModel, setPaymentModel] = useState({
        paymentType: PaymentTypeEnum.CREDIT_CARD,
        paymentDetails: {
            checkoutKey: "",
            checkoutExpiry: new Date(),
            stripeKey: "",
            cardAmount: 0,
            totalAmount: 0,
            totalFees: 0,
            currency: "EUR"
        },
        isFormValid: false
    });
    const [checkoutBillingDetails, setCheckoutBillingDetails] = useState<CheckoutBillingAddressData>();
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [isStepActive, setIsStepActive] = useState(props.isSectionActive);

    //#endregion

    //#region USE EFFECT HOOKS

    useEffect(() => {
        setIsLoading(true);
        handleStepInitialization();
    }, []);

    useEffect(() => {
        setIsLoading(true);

        const amount = parseInt(props.loadAmount);
        if (amount) {
            const newPaymentDetails = paymentModel.paymentDetails;
            newPaymentDetails.cardAmount = amount;
            newPaymentDetails.totalAmount = amount;

            setPaymentModel({
                ...paymentModel,
                paymentDetails: newPaymentDetails
            });
        }
    }, [props.loadAmount]);

    useEffect(() => {
        setIsStepActive(props.isSectionActive);

        if (props.isSectionActive) {
            handleStepInitialization();
        }
    }, [props.isSectionActive]);

    useEffect(() => {
        const isReadyForPayment = paymentModel &&
            paymentModel.paymentDetails &&
            paymentModel.paymentDetails.totalAmount &&
            paymentModel.paymentDetails.cardAmount &&
            paymentModel.paymentDetails.currency;

        if (isReadyForPayment) {
            setTimeout(() => {
                setIsLoading(false);
            }, 500);
        }
    }, [paymentModel]);

    //#endregion

    //#region HANDLERS

    const handleStepInitialization = () => {
        if (!elavonSettings.showCheckoutForm) {
            setPaymentModel(prevModel => ({
                ...prevModel,
                paymentType: PaymentTypeEnum.NONE
            }));

            return;
        }

        const urlParams = new URLSearchParams(window.location.search);
        const spErrQueryParam = urlParams.get('sp-err-code');

        setTimeout(() => {
            try {
                const creditCardRB = document.querySelector("input#stripe__credit-debit-card") as HTMLInputElement;
                if (creditCardRB) {
                    creditCardRB.click();
                }
            } catch (err) {
                console.warn(err);
            }

            if (spErrQueryParam) {
                setTimeout(() => {
                    let errMsg = labels.get(`GiftCard.App.Steps.PreviewAndPay.Card.Errors.ThreeDForm${spErrQueryParam}`);
                    if (!errMsg) {
                        errMsg = labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Errors.ThreeDForm2");
                    }

                    switch (spErrQueryParam) {
                        case "1": console.error(`3D secure authentication returned status, other than 'Ok'.\r\n${errMsg}`); break;
                        case "2": console.error(`Some of the parameters sent to GiftCardService 3D authentication method were invalid.\r\n${errMsg}`); break;
                        case "3": console.error(`Services API returned BadRequest.\r\n${errMsg}`); break;
                        case "4": console.error(`Unauthorized access to Services API 3D secure endpoint.\r\n${errMsg}`); break;
                        case "5": console.error(`Services API returned invalid response.\r\n${errMsg}`); break;
                        case "6": console.error(`An unhandled error occurred processing 3D authentication response.\r\n${errMsg}`); break;
                        default: break;
                    }

                    displayError(errMsg);
                }, 500);

                const divToScrollto = document.querySelector("label[for=stripe__credit-debit-card]");
                if (divToScrollto) {
                    divToScrollto.scrollIntoView();
                }
            }
        }, 600);
    };

    const handleSetCreditCardPaymentType = async () => {
        setIsCardPaymentLoading(true);
        const checkoutSession = await createNewCheckoutSession(false);

        const currPaymentDetails = paymentModel.paymentDetails;
        currPaymentDetails.checkoutKey = checkoutSession.sessionKey;
        currPaymentDetails.checkoutExpiry = checkoutSession.expiry;

        setPaymentModel({
            ...paymentModel,
            paymentType: PaymentTypeEnum.CREDIT_CARD,
            paymentDetails: currPaymentDetails
        });
        setIsCardPaymentLoading(false);
    };

    const handleFormVisibility = (visible: boolean) => {
        const paymentBoxEl = document.getElementById("payment-box");
        if (paymentBoxEl) {
            paymentBoxEl.style.opacity = visible ? "1" : "0.2";
        }

        const paymentSubmitBtnEl = document.getElementById("paymentSubmitBtn") as HTMLButtonElement;
        if (paymentSubmitBtnEl) {
            paymentSubmitBtnEl.disabled = !visible;
        }
    };

    const handleFormUpdate = (isFormValid: boolean, billingDetails: CheckoutBillingAddressData) => {
        setPaymentModel({
            ...paymentModel, isFormValid: isFormValid
        });

        if (isFormValid) {
            setCheckoutBillingDetails(billingDetails);
        }
    };

    const handleCheckout = async () => {
        if (paymentModel.paymentType === PaymentTypeEnum.CREDIT_CARD) {
            if (isValid()) {
                const paymentForm = document.getElementById('checkout-form') as HTMLFormElement;
                if (!paymentForm) {
                    displayError("Unable to find a payment form. Please refresh the page.");
                    return;
                }

                handleFormVisibility(false);

                if (!paymentModel.paymentDetails.checkoutKey || new Date(paymentModel.paymentDetails.checkoutExpiry) <= new Date()) {
                    await createNewCheckoutSession(true);
                }

                // handle opayo tokenisation and submission
                HandleElavonCheckout({
                    sessionKey: paymentModel.paymentDetails.checkoutKey,
                    expiry: paymentModel.paymentDetails.checkoutExpiry
                });

                setTimeout(async () => {
                    await creditCardProcessPayment();
                }, 600);
            } else {
                displayError("Invalid data");
            }
        }
    };

    //#endregion

    //#region HELPERS

    const displayError = (errorMessage: string) => {
        const errorContainer = document.querySelector("#checkout-error") as HTMLDivElement;

        if (errorContainer) {
            errorContainer.style.display = "block";
            errorContainer.textContent = errorMessage;

            //setTimeout(function () {
            //    errorContainer.style.removeProperty("display");
            //    errorContainer.textContent = "";
            //}, 10000);
        }
    };

    const isValid = (): boolean => {
        const amount = paymentModel.paymentDetails.totalAmount;
        const propsAmount = props.basketData.totalAmount;

        if (!amount && !propsAmount) {
            return false;
        }

        if (!props?.basketData?.basketId) {
            return false;
        }

        if (!paymentModel.isFormValid) {
            return false;
        }

        if (!termsAccepted) {
            return false;
        }

        return true;
    };

    const createNewCheckoutSession = async (setSessionKeyModel: boolean): Promise<ICheckoutSessionData> => {
        const amount = paymentModel?.paymentDetails?.totalAmount ?? props?.basketData?.totalAmount;
        if (!amount || amount == 0) {
            console.error("An attempt to create new checkout session was made, but the checkout amount was invalid.");
            return;
        }

        const basketId = props?.basketData?.basketId;
        if (!basketId) {
            console.error("An attempt to create new checkout session was made, but the basketId was invalid.")
            return;
        }

        const newMerchantSession = await DataService.getCheckoutData(basketId, amount, paymentModel?.paymentDetails?.currency ?? "EUR");

        if (!setSessionKeyModel) {
            return newMerchantSession;
        }

        const newPaymentDetailsObj = paymentModel.paymentDetails;
        newPaymentDetailsObj.checkoutKey = newMerchantSession.sessionKey;
        newPaymentDetailsObj.checkoutExpiry = newMerchantSession.expiry;

        setPaymentModel(currPaymentModel => ({
            ...currPaymentModel,
            paymentDetails: newPaymentDetailsObj
        }));

        return newMerchantSession;
    };

    const creditCardProcessPayment = async () => {
        const cardIdentifierField = document.querySelector("#cardIdentifier-field") as HTMLInputElement;
        const cardIdentifier = cardIdentifierField.value;

        if (!cardIdentifier) {
            console.error("Empty card identifier returned from SagePay API.");

            const errorContainer = document.querySelector("#checkout-error") as HTMLDivElement;
            if (errorContainer && (errorContainer.textContent.length === 0 || errorContainer.checkVisibility() === false)) {
                displayError(labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Errors.SagePay"));
            }

            await createNewCheckoutSession(true);
            handleFormVisibility(true);
            return;
        }

        //console.log(`%cSuccessfully tokenised the card details\r\nCard identifier: ${cardIdentifier}`, 'color: green; font-weight: bold;');

        const checkoutRes = await DataService.submitCreditCardPayment('Activate', props.basketData.basketId, paymentModel.paymentDetails.checkoutKey, cardIdentifier, checkoutBillingDetails);
        if (checkoutRes) {
            //console.log('checkoutRes: ', checkoutRes);

            if (checkoutRes.checkoutStatus == CheckoutStatusEnum.Success) {
                if (isNotNullOrEmpty(checkoutRes.thankYouPageUrl)) {
                    window.location.href = checkoutRes.thankYouPageUrl;
                    return;
                } else {
                    console.error("Invalid ThankYou page url provided");
                    window.location.href = "/";
                }
            } else if (checkoutRes.checkoutStatus == CheckoutStatusEnum.Secure3DAuth) {
                const handleThreeDError = async (errorMessage: string) => {
                    const baseErrorMessage = labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Errors.ThreeDForm0");

                    console.error(`${baseErrorMessage} ${errorMessage}`);
                    displayError(baseErrorMessage);
                    await createNewCheckoutSession(true);
                    handleFormVisibility(true);
                };

                HandleThreeDSecureAuth(checkoutRes, handleThreeDError);
                return;
            } else if (checkoutRes.checkoutStatus == CheckoutStatusEnum.Failed || checkoutRes.checkoutStatus == CheckoutStatusEnum.Rejected) {
                displayError(checkoutRes.statusMessage);
                await createNewCheckoutSession(true);
                handleFormVisibility(true);
                return;
            }
        }

        setTimeout(() => {
            handleFormVisibility(true);
        }, 100);
    };

    //#endregion

    return (
        <div className="box">
            {!isStepActive ? (
                <h3>
                    {labels.get("GiftCard.App.ActivateLoadCard.Labels.LoadAndPay")}
                </h3>
            ) : (
                <>
                    <h4>{labels.get("GiftCard.App.ActivateLoadCard.Steps.PaymentType")}</h4>

                    <div className="load-paymentStep">
                        {isLoading ? (
                            <LoadingComponent />
                        ) : (
                            <div id="payment-box">
                                <div className="stripe">
                                    {elavonSettings.showCheckoutForm && (
                                        <>
                                            <input
                                                type="radio"
                                                name="stripe__pay"
                                                id="stripe__credit-debit-card"
                                                onChange={() => handleSetCreditCardPaymentType()}
                                                defaultChecked={paymentModel.paymentType === PaymentTypeEnum.CREDIT_CARD}
                                            />

                                            <div className="services flex-container">
                                                <label htmlFor="stripe__credit-debit-card">
                                                    <span className="label-fs">
                                                        <img src="/static/icons/credit-card-icon.svg" alt="Card" />
                                                        <p>{labels.get("GiftCard.App.ActivateLoadCard.Labels.CreditDebitCard")}</p>
                                                    </span>
                                                </label>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {isCardPaymentLoading && (
                                    <LoadingComponent />
                                )}

                                {(elavonSettings.showCheckoutForm && paymentModel.paymentType === PaymentTypeEnum.CREDIT_CARD) && (
                                    <>
                                        <ElavonPayment
                                            basketId={props?.basketData?.basketId ?? ""}
                                            senderDetails={{
                                                firstName: props?.basketData?.firstName ?? "",
                                                lastName: props?.basketData?.lastName ?? "",
                                                email: props?.basketData?.email ?? "",
                                                phone: props?.basketData?.phone ?? "",
                                            }}
                                            paymentData={{
                                                amount: paymentModel?.paymentDetails?.totalAmount?.toFixed(2) ?? props?.basketData?.totalAmount?.toFixed(2) ?? "0",
                                                currency: paymentModel?.paymentDetails?.currency ?? "EUR"
                                            }}
                                            handleError={displayError}
                                            handleFormUpdate={handleFormUpdate}
                                        />

                                        <div className="flex-container form-field">
                                            <input
                                                type="checkbox"
                                                name="payment-type__accept"
                                                id="payment-type__accept"
                                                checked={termsAccepted}
                                                onChange={(e) => setTermsAccepted(e.target.checked)}
                                            />
                                            <label htmlFor="payment-type__accept" dangerouslySetInnerHTML={{ __html: termsLink }} />
                                        </div>
                                    </>
                                )}

                                <div id="checkout-error"></div>

                                {(elavonSettings.showCheckoutForm) ? (
                                    <>
                                        <button className="btn btn__primary btn__large btn__full_width large-fs"
                                            id="paymentSubmitBtn"
                                            disabled={!isValid()}
                                            onClick={handleCheckout}
                                        >
                                            {labels.get("GiftCard.App.Steps.PreviewAndPay.Btn.Pay")} &euro;{paymentModel?.paymentDetails?.totalAmount?.toFixed(2) ?? props?.basketData?.totalAmount?.toFixed(2) ?? "0"}
                                        </button>
                                    </>
                                ) : (
                                    <p>{labels.get("GiftCard.App.ActivateLoadCard.Tips.NoPaymentMethodAvailable")}</p>
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default LoadAndPayForm;